// REPRISE ORTHOP, CODE TEMPORAIRE : la fiche de l'ancien logiciel sous le mémo du tiers, en lecture seule (GET /common/partner/{id}/reprise-orthop).
// Retrait ou remise : ce dossier + 3 lignes de partner.component (import, imports, balise) — documentation.html#front.common.partner.reprise-orthop
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';

import { environment } from '@app/../environments/environment';

/** Un champ déjà choisi et mis en forme par le serveur (liste blanche de reprise_orthop.rs). */
export interface OrthopField {
	section: string;
	label: string;
	value: string;
	wide: boolean;
}

export interface OrthopSection {
	title: string;
	fields: OrthopField[];
}

/** Les champs arrivent dans l'ordre d'affichage : une section par suite de champs de même section. */
export function groupOrthopFields(fields: OrthopField[]): OrthopSection[] {
	const sections: OrthopSection[] = [];
	for (const f of fields) {
		const last = sections[sections.length - 1];
		if (last?.title === f.section) last.fields.push(f);
		else sections.push({ title: f.section, fields: [f] });
	}
	return sections;
}

@Component({
	selector: 'app-reprise-orthop',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (sections.length) {
			<details class="orthop" id="p2-orthop" open>
				<summary><span class="ttl">Ancien logiciel (orthop)</span><span class="hint">reprise, lecture seule</span></summary>
				@for (s of sections; track s.title) {
					<div class="sec">
						<div class="sec-ttl">{{ s.title }}</div>
						<dl class="grid">
							@for (f of s.fields; track $index) {
								<div class="item" [class.wide]="f.wide"><dt>{{ f.label }}</dt><dd>{{ f.value }}</dd></div>
							}
						</dl>
					</div>
				}
			</details>
		}
	`,
	// Les jetons (--line-soft, --faint…) sont ceux de la fiche tiers, hérités ; la valeur de repli sert hors de la fiche.
	styles: `
		:host { display: block; }
		.orthop { margin-top: 6px; padding-top: 4px; border-top: 1px solid var(--line-soft, #ebe8e3); }
		summary { display: flex; align-items: baseline; gap: 8px; cursor: pointer; color: var(--faint, #a9a298); }
		.ttl { font-size: 10px; letter-spacing: .07em; text-transform: uppercase; }
		.hint { font-size: 10px; font-style: italic; }
		.sec { margin-top: 4px; }
		.sec-ttl { font-size: 10.5px; font-weight: 500; color: var(--muted, #7b7469); margin-bottom: 1px; }
		.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 2px 12px; margin: 0; }
		.item { min-width: 0; }
		.item.wide { grid-column: 1 / -1; }
		dt { font-size: 10px; line-height: 12px; color: var(--faint, #a9a298); }
		dd { margin: 0; font-size: 12.5px; line-height: 16px; color: var(--ink-2, #2c2924); white-space: pre-line; overflow-wrap: anywhere; }
	`,
})
export class RepriseOrthopComponent implements OnDestroy {
	private http = inject(HttpClient);
	private sub?: Subscription;
	sections: OrthopSection[] = [];

	@Input() set partnerId(id: number) {
		this.sub?.unsubscribe();
		this.sections = [];
		if (!(id > 0)) return;
		this.sub = this.http.get<OrthopField[]>(`${environment.server_url}/common/partner/${id}/reprise-orthop`).subscribe({
			next: (fields) => (this.sections = groupOrthopFields(Array.isArray(fields) ? fields : [])),
			// Route retirée du serveur (404) ou lecture en échec : le bloc reste absent, la fiche n'en dépend pas.
			error: (e) => console.warn('[RepriseOrthop] fiche orthop indisponible', e?.status ?? e),
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
